import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { HelixLoader, PageWrapper, UpdatePageHeader } from 'components';
import {
    useDeleteEdgeDictionary,
    useDictionaryList,
    useEdgeDefinition,
    useUpdateEdgeDefinition,
} from 'hooks';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Dictionary,
    EdgeDefinition,
    possiblyNavigateToMetadataSearch,
} from 'lib';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { EDGE_DEFINITION_PATHS } from '../../../../constants/edge-definition-paths';
import { usePermission } from '../../../../core';
import {
    AttributeAssociationItem,
    defaultAttributeAssociationItem,
    defaultEdgeDefinition,
    getSortedDictionaryAttributesFor,
} from '../../edge-definition-helper';
import { updateEdgeDefinitionStyles } from './edit-edge-definition-helper';
import { EditAttributeAssociationWidget } from './EditAttributeAssociationWidget';

export const UpdateEdgeDefinition = (): React.JSX.Element => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { hasPermissions } = usePermission();

    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [edgeDefinition, setEdgeDefinition] = useState<EdgeDefinition>(
        defaultEdgeDefinition
    );
    const [dictionaries, setDictionaries] = useState<Dictionary[]>([]);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [attributeAssociationList, setAttributeAssociationList] = useState<
        AttributeAssociationItem[]
    >([]);

    const { isLoading, isFetched, isSuccess, data } = useEdgeDefinition({
        id: id as string,
    });
    const {
        data: dictionaryData,
        isFetched: isDictionaryDataFetched,
        isSuccess: isDictionaryDataSuccess,
    } = useDictionaryList();
    const { mutate: deleteDictionaryMutation } = useDeleteEdgeDictionary();
    const { mutate: updateEdgeDefinitionMutation } = useUpdateEdgeDefinition();

    useEffect(() => {
        if (
            dictionaryData &&
            isDictionaryDataFetched &&
            isDictionaryDataFetched
        ) {
            setDictionaries(dictionaryData?.data ?? []);
        }
    }, [dictionaryData, isDictionaryDataSuccess, isDictionaryDataFetched]);

    useEffect(() => {
        if (data && isFetched && isSuccess) {
            // we cannot cast EdgeDefinition directly here, so use double assertion rather than any.
            const edgeData: EdgeDefinition = data as unknown as EdgeDefinition;
            setEdgeDefinition(edgeData);

            // get default conditions to populate attribute association section.
            const defaultConditions: AttributeAssociationItem[] = (
                edgeData.condition ?? []
            ).map((condition) => {
                return {
                    ...defaultAttributeAssociationItem,
                    attributeAssociation: {
                        leftSideValue: condition.fromCol,
                        rightSideValue: condition.toCol,
                    },
                };
            });
            setAttributeAssociationList(defaultConditions);
        }
    }, [data, isFetched, isSuccess]);

    const dictionaryName = edgeDefinition?.name ?? '--';
    const fromDictionary = edgeDefinition?.fromDictionary ?? '--';
    const toDictionary = edgeDefinition?.toDictionary ?? '--';
    const fromAttributeList = getSortedDictionaryAttributesFor(
        fromDictionary,
        dictionaries
    );
    const toAttributeList = getSortedDictionaryAttributesFor(
        toDictionary,
        dictionaries
    );
    const hasDeletePermission = hasPermissions('deleteMetadata');

    const handleEdgeDefinitionNavigation = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            EDGE_DEFINITION_PATHS.LANDING_PAGE,
            searchParams
        );
    };

    const updateEdgeDefinitionForm = (): void => {
        setIsChangesUnsaved(false);
        const condition = {
            condition: attributeAssociationList.map((association) => {
                return {
                    fromCol: association.attributeAssociation.leftSideValue,
                    toCol: association.attributeAssociation.rightSideValue,
                };
            }),
        };
        updateEdgeDefinitionMutation(
            { edgeId: id ?? '', condition },
            {
                onSuccess: () => {
                    setTimeout(() => {
                        handleEdgeDefinitionNavigation();
                    }, 500);
                },
            }
        );
    };

    const handleDeleteConfirmation = (): void => {
        deleteDictionaryMutation(
            { edgeId: id as string },
            {
                onSuccess: () => {
                    setTimeout(() => {
                        handleEdgeDefinitionNavigation();
                    }, 500);
                },
            }
        );
    };

    const possiblyUpdateEdgeDefinitionForm = (): void => {
        const permission = hasPermissions('editMetadata');
        if (permission) {
            updateEdgeDefinitionForm();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const onSubmit = (): void => {
        if (
            attributeAssociationList.length > 0 &&
            attributeAssociationList.every(
                (association) =>
                    !association.leftSideError && !association.rightSideError
            )
        ) {
            possiblyUpdateEdgeDefinitionForm();
        }
    };

    const handleCancelButton = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            handleEdgeDefinitionNavigation();
        }
    };

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        handleEdgeDefinitionNavigation();
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const handleAttributeAssociationListChange = (
        newList: AttributeAssociationItem[]
    ): void => {
        setAttributeAssociationList(newList);
        setIsChangesUnsaved(true);
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    if (isLoading) {
        return <HelixLoader />;
    }

    return (
        <PageWrapper title='Edit Edge Definition' includeBreadcrumbs={true}>
            <UpdatePageHeader
                headingText={'General Info'}
                deleteMenuItemText={'Delete Edge'}
                handleSaveButton={onSubmit}
                handleCancelButton={handleCancelButton}
                handleCancelModal={handleCancelModal}
                handleCancelModalConfirmation={handleCancelModalConfirmation}
                isLoading={false}
                readOnly={false}
                checkForExternalDeletePermission
                hasDeletePermission={hasDeletePermission}
                isChangesUnsaved={isChangesUnsaved}
                openCancelModal={openCancelModal}
                unsavedChangesConfirmationText={
                    'This edge definition has unsaved changes. Are you sure you want to close without saving? You will lose all changes.'
                }
                confirmationModalConfig={{
                    confirmationButtonText: 'Yes, Delete',
                    confirmationHeadingText: 'Confirm Edge Definition Deletion',
                    handleConfirmation: handleDeleteConfirmation,
                    confirmationCancelButtonText: 'No, Cancel',
                    hasConfirmationTextField: false,
                    confirmationSubHeadingText: (
                        <>
                            Are you sure you would like to delete your edge
                            definition?
                        </>
                    ),
                }}
            />
            <Grid sx={updateEdgeDefinitionStyles.generalInfoContainer}>
                <Grid sx={updateEdgeDefinitionStyles.infoDisplayContainer}>
                    <Typography sx={updateEdgeDefinitionStyles.sectionLabel}>
                        Name
                    </Typography>
                    <Typography sx={updateEdgeDefinitionStyles.sectionValue}>
                        {dictionaryName}
                    </Typography>
                </Grid>
                <Grid sx={updateEdgeDefinitionStyles.dictionaryInfoContainer}>
                    <Grid
                        sx={updateEdgeDefinitionStyles.fromDictionaryContainer}
                    >
                        <Typography
                            sx={updateEdgeDefinitionStyles.sectionLabel}
                        >
                            From Dictionary
                        </Typography>
                        <Typography
                            sx={updateEdgeDefinitionStyles.sectionValue}
                        >
                            {fromDictionary}
                        </Typography>
                    </Grid>
                    <Grid sx={updateEdgeDefinitionStyles.toDictionaryContainer}>
                        <Typography
                            sx={updateEdgeDefinitionStyles.sectionLabel}
                        >
                            To Dictionary
                        </Typography>
                        <Typography
                            sx={updateEdgeDefinitionStyles.sectionValue}
                        >
                            {toDictionary}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                sx={updateEdgeDefinitionStyles.attributeAssociationsContainer}
            >
                <Typography sx={updateEdgeDefinitionStyles.sectionText}>
                    Attribute Association
                </Typography>
                <Grid sx={updateEdgeDefinitionStyles.bodyContainer}>
                    <EditAttributeAssociationWidget
                        onAttributeAssociationListChange={
                            handleAttributeAssociationListChange
                        }
                        attributeAssociationList={attributeAssociationList}
                        leftSideSelectionOptions={fromAttributeList}
                        rightSideSelectionOptions={toAttributeList}
                    />
                </Grid>
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </PageWrapper>
    );
};
